import { IconButton, LinearProgress, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { FormikTextField, Section } from "components/disw";
import { DropDownBox } from "components/disw/DropDownBox";
import {
  RestrictedSolutionRealm,
  SolutionPermission
} from "features/access-management";
import { LimitType } from "model/schema/globalTypes";
import React from "react";
import theme from "utils/theme";
import addEmailIcon from "./icons/addEmail.svg";
import editIcon from "./icons/edit.svg";
import trashIcon from "./icons/trash.svg";
import { ServiceProductNotificationTriggers } from "./types";

export type NotificationTriggerError = {
  email?: string;
  thresholds?: string[];
};

interface NotificationSettingsProps {
  notificationTriggers: ServiceProductNotificationTriggers[];
  errors?: NotificationTriggerError[];
  onEmailChange?: (email: string, idx: number) => void;
  onThresholdChange: (e: { value: number[] }, idx: number) => void;
  onAddRow?: () => void;
  onDeleteRow?: (idx: number) => void;
  limitType?: LimitType;
  isReadOnly?: boolean;
  onEditToggle?: () => void;
  emailFieldWidth?: number;
  loading?: boolean;
  errorMessage?: string;
}

interface NotificationRowProps {
  idx: number;
  row: ServiceProductNotificationTriggers;
  rowError: NotificationTriggerError;
  thresholdOptions: number[];
  onEmailChange?: (email: string, idx: number) => void;
  onThresholdChange: (e: { value: number[] }, idx: number) => void;
  onDeleteRow?: (idx: number) => void;
  isReadOnly: boolean;
  emailFieldWidth: number;
}

const NotificationRow: React.FC<NotificationRowProps> = ({
  idx,
  row,
  rowError,
  thresholdOptions,
  onEmailChange,
  onThresholdChange,
  onDeleteRow,
  isReadOnly,
  emailFieldWidth
}) => {
  const classes = useStyles();
  const thresholdErrorText = Array.isArray(rowError.thresholds)
    ? rowError.thresholds.length > 0
      ? rowError.thresholds.join(", ")
      : undefined
    : typeof rowError.thresholds === "string"
    ? rowError.thresholds
    : undefined;
  const thresholdIsError = Boolean(thresholdErrorText);

  const combinedThresholdOptions = Array.from(
    new Set([...thresholdOptions, ...row.thresholds])
  ).sort((a, b) => a - b);

  return (
    <div className={classes.row} key={idx}>
      <div
        style={{
          width: emailFieldWidth,
          marginBottom: 16,
          marginRight: 16
        }}
      >
        <FormikTextField
          name={`notificationTriggers[${idx}].email`}
          label="Email"
          placeholder="Enter or select email"
          type="email"
          error={!!rowError.email}
          errorMessage={
            typeof rowError.email === "string" ? rowError.email : undefined
          }
          readOnly={isReadOnly}
          onChange={e => {
            onEmailChange && onEmailChange(e.target.value, idx);
          }}
        />
      </div>
      <div style={{ width: 280, marginBottom: 16 }}>
        <DropDownBox
          label="Usage threshold"
          dataSource={combinedThresholdOptions}
          value={row.thresholds}
          displayExpr={(item: number) => `${Math.round(item * 100)}%`}
          placeholder="Select threshold"
          onValueChanged={(e: { value: number[] }) => onThresholdChange(e, idx)}
          isError={thresholdIsError}
          errorText={thresholdErrorText}
          readOnly={isReadOnly}
        />
      </div>
      {!isReadOnly && (
        <div className={classes.trashBtnContainer}>
          <button
            type="button"
            className={classes.trashBtn}
            aria-label="Delete notification"
            onClick={() => onDeleteRow && onDeleteRow(idx)}
          >
            <img
              src={trashIcon}
              alt="Delete"
              width={16}
              height={16}
              className={classes.trashIcon}
            />
          </button>
        </div>
      )}
    </div>
  );
};

const useStyles = makeStyles({
  editIconBtn: {
    position: "absolute",
    top: 2,
    right: 0,
    zIndex: 2,
    borderRadius: 0,
    outline: "none",
    width: 36,
    height: 28,
    paddingRight: 8,
    "&:hover": {
      background: "transparent"
    },
    "&:focus": {
      border: "2px solid #0F789B"
    }
  },
  editIconImg: {
    width: 16,
    height: 16
  },
  info: {
    fontFamily: "Segoe UI",
    fontWeight: 400,
    fontStyle: "normal",
    fontSize: "12px !important",
    lineHeight: "16px",
    letterSpacing: 0,
    color: "#1E1E1E",
    marginBottom: "16px !important"
  },
  row: {
    display: "flex",
    gap: 8,
    alignItems: "center"
  },
  trashBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 0,
    display: "flex",
    alignItems: "center"
  },
  trashIcon: {
    color: "#005F87"
  },
  addEmailIcon: {
    width: 16,
    height: 16
  },
  addEmailText: {
    fontFamily: "Segoe UI",
    fontWeight: 600,
    fontStyle: "Semibold",
    fontSize: 12,
    lineHeight: "16px",
    letterSpacing: 0,
    textAlign: "center",
    color: "#005F87"
  },
  dropDownBox: {
    marginBottom: 16
  },
  trashBtnContainer: {
    display: "flex",
    alignItems: "flex-end",
    height: 32,
    marginBottom: 10
  },
  addEmailBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 0,
    display: "flex",
    alignItems: "center"
  },
  addEmailContainer: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    justifyContent: "flex-start",
    padding: theme.spacing(0.5, 1)
  }
});

const NotificationSettings: React.FC<NotificationSettingsProps> = ({
  notificationTriggers,
  errors = [],
  onEmailChange,
  onThresholdChange = () => {},
  onAddRow,
  onDeleteRow,
  limitType,
  isReadOnly = false,
  onEditToggle,
  emailFieldWidth = 288,
  loading = false,
  errorMessage
}) => {
  const classes = useStyles();
  const thresholdOptions =
    limitType === LimitType.Soft
      ? [0.5, 0.6, 0.7, 0.8, 0.9, 0.95, 0.99, 1, 1.2]
      : [0.5, 0.6, 0.7, 0.8, 0.9, 0.95, 0.99, 1];
  return (
    <Section name="Notifications">
      {loading && <LinearProgress style={{ marginBottom: 16 }} />}
      {errorMessage && (
        <Typography color="error" style={{ marginBottom: 16 }}>
          {errorMessage}
        </Typography>
      )}
      <div>
        {onEditToggle && (
          <RestrictedSolutionRealm
            silent
            requiredPermissions={[SolutionPermission.ChangeServiceProducts]}
          >
            <IconButton
              className={classes.editIconBtn}
              aria-label={"Edit"}
              onClick={onEditToggle}
            >
              <img
                src={editIcon}
                alt={"Edit"}
                className={classes.editIconImg}
              />
            </IconButton>
          </RestrictedSolutionRealm>
        )}
        {!isReadOnly && (
          <Typography className={classes.info}>
            Entered user will receive email notifications after exceeding the
            selected usage thresholds..
          </Typography>
        )}
        {notificationTriggers.map((row, idx) => (
          <NotificationRow
            key={idx}
            idx={idx}
            row={row}
            rowError={errors[idx] || {}}
            thresholdOptions={thresholdOptions}
            onEmailChange={onEmailChange}
            onThresholdChange={onThresholdChange}
            onDeleteRow={onDeleteRow}
            isReadOnly={isReadOnly}
            emailFieldWidth={emailFieldWidth}
          />
        ))}

        {!isReadOnly && (
          <div>
            <div
              className={classes.addEmailContainer}
              style={{ cursor: "pointer" }}
              onClick={onAddRow}
              role="button"
              tabIndex={0}
              aria-label="Add email"
            >
              <IconButton
                className={classes.addEmailBtn}
                tabIndex={-1}
                aria-label="Add email"
              >
                <img
                  src={addEmailIcon}
                  alt="Add email"
                  className={classes.addEmailIcon}
                />
              </IconButton>
              <span className={classes.addEmailText}>Add email</span>
            </div>
          </div>
        )}
      </div>
    </Section>
  );
};

export default NotificationSettings;
